import _ from 'lodash';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { ImageService } from 'app/core/services/image.service';
import { selectCampaignSelected } from 'app/campaign/features/campaign.selectors';
import { CampaignService } from 'app/campaign/services/campaign.service';
import { CampaignSponsorSchema } from '../../structure/sponsor-group';
import { filter, first, takeUntil } from 'rxjs/operators';
import { ToastService } from 'app/toast/services/toast.service';
import { ChooseImageModalService } from 'app/core/services/choose-image-modal.service';

@Component({
  selector: 'app-campaign-sponsor',
  templateUrl: './campaign-sponsor.component.html',
  styleUrls: ['./campaign-sponsor.component.scss']
})
export class CampaignSponsorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() formGroupCampaign: any = null;
  @Input() selected: any = null;
  @Input() selectedIndex: number = null;

  @Output() onChangeData: EventEmitter<any> = new EventEmitter();

  public campaign = this.store.pipe(
    select(selectCampaignSelected)
  );

  public sponsors: UntypedFormArray = null;
  public sponsor: UntypedFormGroup = null;
  public sponsorSubmitted: boolean = false;

  public value: any = null;

  public takeUntilSubject: Subject<any> = new Subject();

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private campaignService: CampaignService,
    private imageService: ImageService,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private chooseImageModalService: ChooseImageModalService,
  ) { }

  ngOnInit(): void {
    const { selected } = this;
    this.sponsors = this.formGroupCampaign.get('sponsors') as UntypedFormArray;

    if (this.selected) {
      const imageUrl = selected.get('imageUrl').value === '/images/org_logo_default_v2.svg' ?
        '/images/blank_2x1.png' :
        selected.get('imageUrl').value;

      selected.get('imageDataUrl').setValue(imageUrl);
      selected.get('imageUrl').setValue(imageUrl);

      this.sponsor = this.selected;
      this.value = _.cloneDeep(this.selected.value);
    } else {
      this.sponsor = this.fb.group({});
      this.campaignService.generateForm(this.sponsor, CampaignSponsorSchema);
    }

    this.reNgOnInit();
  }

  reNgOnInit() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();

    this.takeUntilSubject = new Subject();

    this.sponsor.get('imageFile').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntil(this.takeUntilSubject)
    ).subscribe(value => {
      if (value.name && value.type) {
        const windowURL = window.URL || window.webkitURL;
        const url = windowURL.createObjectURL(value);
        this.imageService.cropper({
          url: this.domSanitizer.bypassSecurityTrustUrl(url),
          id: 'sponsor-image',
          name: value.name,
          type: value.type,
        });
      }
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'sponsor-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      this.sponsor.get('imageDataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'sponsor-image'),
      takeUntil(this.takeUntilSubject)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.sponsor.get('imageUrl').setValue(result.imageUrl);
      }
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const selected = _.get(changes, 'selected.currentValue', this.selected);
    if(selected) {
      const imageUrl = selected.get('imageUrl').value === '/images/org_logo_default_v2.svg' ?
        '/images/blank_2x1.png':
        selected.get('imageUrl').value;
      selected.get('imageDataUrl').setValue(imageUrl);
      selected.get('imageUrl').setValue(imageUrl);

      this.sponsor = this.selected;
      this.value = _.cloneDeep(this.selected.value);
    }

    if(this.sponsor) {
      this.reNgOnInit();
    }
  }

  ngOnDestroy() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }

  onCancel() {
    if(this.selected) {
      this.sponsor.reset(this.value);
    }
    this.onChangeData.next(false);
  }

  saveCampaignSponsor() {
    const { sponsor, selected } = this;
    this.sponsorSubmitted = true;

    if (sponsor.invalid || this.sponsor.controls.imageUrl.value === '/images/blank_2x1.png') {
      sponsor.markAllAsTouched();
      return false;
    }

    if (selected) {
      const imageUrl = selected.get('imageUrl').value === '/images/blank_2x1.png' ?
        '':
        selected.get('imageUrl').value;
      selected.get('imageUrl').setValue(imageUrl);

      this.campaign.pipe(
        first(),
        takeUntil(this.takeUntilSubject)
      ).subscribe(campaign => {
        const sponsorValue = this.campaignService.generateRequestObject(selected, CampaignSponsorSchema);

        this.sponsors.removeAt(this.selectedIndex);
        this.addCampaignSponsor(this.selectedIndex);
        /*
        this.store.dispatch({
          type: updateCampaignSponsor.type,
          payload: { campaign, sponsor: sponsorValue }
        });
        */
        return this.onCancel();
      });
    } else {
      this.addCampaignSponsor();
    }
  }

  editCampaignSponsor(event) {
    const { sponsor, index } = event;

    const group = this.fb.group({});
    this.campaignService.generateForm(group, CampaignSponsorSchema);
    this.campaignService.initializeForm(group, sponsor.value, CampaignSponsorSchema);

    this.campaign.pipe(
      first(),
      takeUntil(this.takeUntilSubject)
    ).subscribe(campaign => {
      const campaignId = _.get(campaign, 'id', null);
      const sponsorId = _.get(sponsor, 'value.id', null);
      const sponsorValue = this.campaignService.generateRequestObject(group, CampaignSponsorSchema);

      if(_.isNumber(index)) {
        this.sponsors.insert(index, group);
      } else {
        this.sponsors.push(group);
      }

      if(_.isNull(campaignId)) {
        /*
        this.sponsors.push(group);
        */
        return this.onChangeData.next(true);
      }

      if(_.isNull(sponsorId)) {
        /*
        this.store.dispatch({
          type: addCampaignSponsor.type,
          payload: { campaign, sponsor: sponsorValue }
        });
        */
        return this.onCancel();
      }

      /*
      this.store.dispatch({
        type: updateCampaignSponsor.type,
        payload: { campaign, sponsor: sponsorValue }
      });
      */
      return this.onCancel();
    });

    this.sponsorSubmitted = false;
  }

  addCampaignSponsor(index = null) {
    const { sponsor, sponsor: { value } } = this;

    const imageUrl = value.imageUrl === '/images/blank_2x1.png' ? '' : value.imageUrl;
    sponsor.get('imageUrl').setValue(imageUrl);

    const group = this.fb.group({});
    this.campaignService.generateForm(group, CampaignSponsorSchema);
    this.campaignService.initializeForm(group, this.sponsor.value, CampaignSponsorSchema);

    sponsor.reset();
    sponsor.get('imageUrl').setValue("/images/blank_2x1.png");

    this.campaign.pipe(
      first(),
      takeUntil(this.takeUntilSubject)
    ).subscribe(campaign => {
      const campaignId = _.get(campaign, 'id', null);
      const sponsorId = _.get(sponsor, 'value.id', null);
      const sponsorValue = this.campaignService.generateRequestObject(group, CampaignSponsorSchema);

      if(_.isNumber(index)) {
        this.sponsors.insert(index, group);
      } else {
        this.sponsors.push(group);
      }

      if(_.isNull(campaignId)) {
        /*
        this.sponsors.push(group);
        */
        return this.onChangeData.next(true);
      }

      if(_.isNull(sponsorId)) {
        /*
        this.store.dispatch({
          type: addCampaignSponsor.type,
          payload: { campaign, sponsor: sponsorValue }
        });
        */
        return this.onCancel();
      }

      /*
      this.store.dispatch({
        type: updateCampaignSponsor.type,
        payload: { campaign, sponsor: sponsorValue }
      });
      */
      return this.onCancel();
    });
  }

  openChooseImageModal(type = 0) { // 0 for 2x1, 1 for 1x1
    const params = {
      start: 0,
      returnCount: 40,
      filterBy:[type]
    }

    this.campaignService.getOrganizationImages(params).pipe(
      takeUntil(this.takeUntilSubject)
    ).subscribe((imageList: any)=> {
      const list = imageList || [];
      const images = list.filter((image) => image.type === type)
      const modalRef = this.chooseImageModalService.open({ images, type });
    
      modalRef.onClose.pipe(
        takeUntil(this.takeUntilSubject)
      ).subscribe((imageUrl) =>{
        if(imageUrl) {
          this.sponsor.get(`imageDataUrl`).setValue(imageUrl);
          this.sponsor.get(`imageUrl`).setValue(imageUrl);
          this.toastService.show('success', 'Added Successfully!', {delay: 2000});
        }
        this.chooseImageModalService.modalRef = null;
      })
    })
  }

  resetImage() {
    this.sponsor.get('imageFile').setValue(null);
    this.sponsor.get('imageDataUrl').setValue('/images/blank_2x1.png');
    this.sponsor.get('imageUrl').setValue('/images/blank_2x1.png');
  }

  getSponsorImageErrorMessage() {
    if (this.sponsorSubmitted === false) {
      return;
    }

    if (this.sponsor.get('imageUrl').value === '/images/blank_2x1.png') {
      return 'Image cannot be empty';
    }

    return '';
  }

}
